/* Position: the board and the pieces as typed arrays. Functions here either read or mutate explicitly (documented). */
import { BLACK, CELLS, MAX_PIECES, N, OCTAGON, WHITE } from './types.ts';
import type { ShapeId, Side } from './types.ts';

export interface Position {
  /** cell -> piece index, or -1 */
  board: Int8Array;
  shape: Uint8Array;
  owner: Uint8Array;
  row: Int8Array;
  col: Int8Array;
  alive: Uint8Array;
  /** slot has ever held a piece (so a dead used slot is a lost piece) */
  used: Uint8Array;
  /** bonds[i] = bitmask of piece indices bonded to i (symmetric). A body is a connected component of bonds. */
  bonds: Uint32Array;
  toMove: Side;
}

export interface LayoutPiece { shape: ShapeId; r: number; c: number }
/** White's nine pieces; Black's are the same rotated 180 degrees (D1). */
export type Layout = readonly LayoutPiece[];

export const cellOf = (r: number, c: number): number => r * N + c;
export const rowOf = (cell: number): number => Math.floor(cell / N);
export const colOf = (cell: number): number => cell % N;
export const inb = (r: number, c: number): boolean => r >= 0 && r < N && c >= 0 && c < N;
/** "E5" style notation: column letter A-I, row number 1-9. */
export const cellName = (r: number, c: number): string => String.fromCharCode(65 + c) + (r + 1);
export const cellNameOf = (cell: number): string => cellName(rowOf(cell), colOf(cell));
export function parseCell(name: string): [number, number] {
  return [Number(name.slice(1)) - 1, name.toUpperCase().charCodeAt(0) - 65];
}

export function emptyPosition(): Position {
  return {
    board: new Int8Array(CELLS).fill(-1),
    shape: new Uint8Array(MAX_PIECES),
    owner: new Uint8Array(MAX_PIECES),
    row: new Int8Array(MAX_PIECES).fill(-1),
    col: new Int8Array(MAX_PIECES).fill(-1),
    alive: new Uint8Array(MAX_PIECES),
    used: new Uint8Array(MAX_PIECES),
    bonds: new Uint32Array(MAX_PIECES),
    toMove: WHITE,
  };
}

export function clonePosition(p: Position): Position {
  return {
    board: p.board.slice(), shape: p.shape.slice(), owner: p.owner.slice(), row: p.row.slice(), col: p.col.slice(),
    alive: p.alive.slice(), used: p.used.slice(), bonds: p.bonds.slice(), toMove: p.toMove,
  };
}

/** Put piece `i` on the board (mutates). Throws if the cell is taken. */
export function place(p: Position, i: number, shape: ShapeId, side: Side, r: number, c: number): void {
  const cell = cellOf(r, c);
  if (p.board[cell] >= 0) throw new Error(`cell ${cellName(r, c)} is occupied`);
  p.shape[i] = shape; p.owner[i] = side; p.row[i] = r; p.col[i] = c; p.alive[i] = 1; p.used[i] = 1; p.bonds[i] = 0;
  p.board[cell] = i;
}

/** Bond two pieces (mutates). Callers ensure they are adjacent with opposite poles. */
export function bond(p: Position, a: number, b: number): void {
  p.bonds[a] |= 1 << b; p.bonds[b] |= 1 << a;
}

/** Take a piece off the board and dissolve its bonds (mutates). */
export function removePiece(p: Position, i: number): void {
  if (p.alive[i] && p.row[i] >= 0) {
    const cell = cellOf(p.row[i], p.col[i]);
    if (p.board[cell] === i) p.board[cell] = -1;
  }
  let m = p.bonds[i];
  while (m) { const j = 31 - Math.clz32(m); m &= ~(1 << j); p.bonds[j] &= ~(1 << i); }
  p.bonds[i] = 0; p.alive[i] = 0; p.row[i] = -1; p.col[i] = -1;
}

/** Start position from a White layout: White pieces are indices 0-8, Black 9-17 (rotated 180 degrees). */
export function positionFromLayout(layout: Layout): Position {
  if (layout.length !== 9) throw new Error('a layout has nine pieces');
  const p = emptyPosition();
  layout.forEach((lp, i) => {
    place(p, i, lp.shape, WHITE, lp.r, lp.c);
    place(p, 9 + i, lp.shape, BLACK, N - 1 - lp.r, N - 1 - lp.c);
  });
  return p;
}

export function bitsOf(mask: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < MAX_PIECES; i++) if (mask & (1 << i)) out.push(i);
  return out;
}

/** Pieces bonded (transitively) to `i`, including i, ascending. */
export function bodyOf(p: Position, i: number): number[] {
  let seen = 1 << i, frontier = 1 << i;
  while (frontier) {
    const j = 31 - Math.clz32(frontier); frontier &= ~(1 << j);
    const nb = p.bonds[j] & ~seen; seen |= nb; frontier |= nb;
  }
  return bitsOf(seen);
}

/** label[i] = smallest index in i's body (a stable label), or -1 when dead. */
export function labelBodies(p: Position): Int8Array {
  const label = new Int8Array(MAX_PIECES).fill(-1);
  for (let i = 0; i < MAX_PIECES; i++) {
    if (!p.alive[i] || label[i] >= 0) continue;
    for (const m of bodyOf(p, i)) label[m] = i;
  }
  return label;
}

export function countAlive(p: Position, side: Side): number {
  let n = 0;
  for (let i = 0; i < MAX_PIECES; i++) if (p.alive[i] && p.owner[i] === side) n++;
  return n;
}
export function countLost(p: Position, side: Side): number {
  let n = 0;
  for (let i = 0; i < MAX_PIECES; i++) if (p.used[i] && !p.alive[i] && p.owner[i] === side) n++;
  return n;
}
export function coreAlive(p: Position, side: Side): boolean {
  for (let i = 0; i < MAX_PIECES; i++) if (p.alive[i] && p.owner[i] === side && p.shape[i] === OCTAGON) return true;
  return false;
}
export function hasCore(p: Position, side: Side): boolean {
  for (let i = 0; i < MAX_PIECES; i++) if (p.used[i] && p.owner[i] === side && p.shape[i] === OCTAGON) return true;
  return false;
}

/** Identity-independent key: what stands on each cell, the bonds between cells, and the side to move. Used for ko and repetition (D3). */
export function positionKey(p: Position): string {
  let s = '';
  for (let cell = 0; cell < CELLS; cell++) {
    const i = p.board[cell];
    if (i < 0) { s += '.'; continue; }
    const r = p.row[i], c = p.col[i];
    let bits = 0;
    // bonds toward E, N, NE and NW cover every adjacency exactly once
    if (c + 1 < N) { const j = p.board[cell + 1]; if (j >= 0 && (p.bonds[i] & (1 << j))) bits |= 1; }
    if (r + 1 < N) {
      const j = p.board[cell + N]; if (j >= 0 && (p.bonds[i] & (1 << j))) bits |= 2;
      if (c + 1 < N) { const k = p.board[cell + N + 1]; if (k >= 0 && (p.bonds[i] & (1 << k))) bits |= 4; }
      if (c - 1 >= 0) { const k = p.board[cell + N - 1]; if (k >= 0 && (p.bonds[i] & (1 << k))) bits |= 8; }
    }
    s += String.fromCharCode(48 + ((p.shape[i] + 4 * p.owner[i]) << 4) + bits);
  }
  return s + (p.toMove === WHITE ? 'w' : 'b');
}

/** The same position with piece indices permuted (perm[old] = new). For order-independence tests (D6). */
export function relabel(p: Position, perm: readonly number[]): Position {
  const q = emptyPosition(); q.toMove = p.toMove;
  for (let i = 0; i < MAX_PIECES; i++) {
    const j = perm[i];
    q.shape[j] = p.shape[i]; q.owner[j] = p.owner[i]; q.row[j] = p.row[i]; q.col[j] = p.col[i]; q.alive[j] = p.alive[i]; q.used[j] = p.used[i];
    let m = 0; for (const b of bitsOf(p.bonds[i])) m |= 1 << perm[b]; q.bonds[j] = m;
    if (p.alive[i]) q.board[cellOf(p.row[i], p.col[i])] = j;
  }
  return q;
}

/** The position seen from the other side: rotated 180 degrees with owners swapped. For symmetry tests (D1). */
export function rotate180(p: Position): Position {
  const q = emptyPosition(); q.toMove = p.toMove === WHITE ? BLACK : WHITE;
  for (let i = 0; i < MAX_PIECES; i++) {
    q.shape[i] = p.shape[i]; q.owner[i] = p.owner[i] === WHITE ? BLACK : WHITE; q.alive[i] = p.alive[i]; q.used[i] = p.used[i]; q.bonds[i] = p.bonds[i];
    if (p.alive[i]) { q.row[i] = N - 1 - p.row[i]; q.col[i] = N - 1 - p.col[i]; q.board[cellOf(q.row[i], q.col[i])] = i; }
  }
  return q;
}

/** ASCII board for logs and tests. Upper-case = Black, lower-case = White; t s r o = shape; digit = body label. */
export function render(p: Position): string {
  const label = labelBodies(p);
  const rows: string[] = [];
  for (let r = N - 1; r >= 0; r--) {
    let line = String(r + 1) + ' ';
    for (let c = 0; c < N; c++) {
      const i = p.board[cellOf(r, c)];
      if (i < 0) { line += ' . '; continue; }
      const ch = 'tsro'[p.shape[i]];
      const inBody = p.bonds[i] !== 0;
      line += (p.owner[i] === BLACK ? ch.toUpperCase() : ch) + (inBody ? String(label[i] % 10) : ' ') + ' ';
    }
    rows.push(line);
  }
  rows.push('   ' + 'ABCDEFGHI'.split('').join('  '));
  return rows.join('\n');
}
